/**
 * useVoiceWebSocket — Real-time voice via /api/voice/stream WebSocket
 * KURAL 99: truncated=true → 'Ekrana yazildi, oku?' prompt
 * KURAL 100: Cross-platform WebSocket
 */
import { useCallback, useRef, useState } from 'react'

export type VoiceState = 'idle' | 'listening' | 'thinking' | 'speaking' | 'error'

interface VoiceServerMsg {
  type: 'transcript' | 'ai_response' | 'read_aloud' | 'status' | 'error'
  text?: string
  audio_b64?: string
  truncated?: boolean
  state?: string
  message?: string
}

const WS_URL = import.meta.env.DEV
  ? 'ws://localhost:8888/api/voice/stream'
  : 'wss://api.runai.live/api/voice/stream'

export function useVoiceWebSocket() {
  const [voiceState, setVoiceState] = useState<VoiceState>('idle')
  const [transcript, setTranscript] = useState('')
  const [aiText, setAiText] = useState('')
  const [fullText, setFullText] = useState('')
  const [truncated, setTruncated] = useState(false)
  const [language, setLanguage] = useState<string | null>(null)
  const [isConnected, setIsConnected] = useState(false)

  const wsRef = useRef<WebSocket | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const sourceRef = useRef<AudioBufferSourceNode | null>(null)

  const playAudio = useCallback(async (b64: string) => {
    if (!audioCtxRef.current) audioCtxRef.current = new AudioContext()
    const ctx = audioCtxRef.current
    const binary = atob(b64)
    const buf = new ArrayBuffer(binary.length)
    const view = new Uint8Array(buf)
    for (let i = 0; i < binary.length; i++) view[i] = binary.charCodeAt(i)
    const audioBuf = await ctx.decodeAudioData(buf)
    sourceRef.current?.stop()
    const source = ctx.createBufferSource()
    source.buffer = audioBuf
    source.connect(ctx.destination)
    source.onended = () => setVoiceState('idle')
    source.start()
    sourceRef.current = source
    setVoiceState('speaking')
  }, [])

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return
    const token = localStorage.getItem('runai_token') ?? ''
    const ws = token
      ? new WebSocket(WS_URL, ['voice.runai', token])
      : new WebSocket(WS_URL)
    wsRef.current = ws

    ws.onopen = () => {
      setIsConnected(true)
      setVoiceState('idle')
    }

    ws.onmessage = (e) => {
      try {
        const msg: VoiceServerMsg = JSON.parse(e.data)
        switch (msg.type) {
          case 'transcript':
            if (msg.text) setTranscript(msg.text)
            break
          case 'ai_response':
            if (msg.text) setAiText(msg.text)
            setTruncated(!!msg.truncated)
            if (msg.audio_b64) void playAudio(msg.audio_b64)
            break
          case 'status':
            if (msg.state?.startsWith('language=')) {
              const lang = msg.state.split('language=')[1]?.split(';')[0]
              if (lang) setLanguage(lang)
            } else if (msg.state) {
              setVoiceState(msg.state as VoiceState)
            }
            break
          case 'read_aloud':
            if (msg.text) setFullText(msg.text)
            break
          case 'error':
            setVoiceState('error')
            break
        }
      } catch { /* ignore */ }
    }

    ws.onclose = () => { setIsConnected(false); setVoiceState('idle') }
    ws.onerror = () => { setIsConnected(false); setVoiceState('error') }
  }, [playAudio])

  const disconnect = useCallback(() => {
    wsRef.current?.close()
    wsRef.current = null
    sourceRef.current?.stop()
    setIsConnected(false)
    setVoiceState('idle')
  }, [])

  const sendAudioChunk = useCallback((data: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN)
      wsRef.current.send(JSON.stringify({ type: 'audio_chunk', data }))
  }, [])

  const sendInterrupt = useCallback(() => {
    sourceRef.current?.stop()
    if (wsRef.current?.readyState === WebSocket.OPEN)
      wsRef.current.send(JSON.stringify({ type: 'interrupt' }))
    setVoiceState('idle')
  }, [])

  return {
    voiceState, transcript, aiText, fullText, truncated, isConnected, language,
    connect, disconnect, sendAudioChunk, sendInterrupt, setVoiceState,
  }
}
